import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Plus, Trash2, Save, Palette, Image as ImageIcon, StickyNote, Cake, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_COLORS = ['#FFF3B0', '#FFC8DD', '#BBF7D0', '#A2D2FF', '#FDE68A', '#CDB4DB'];

function ZinniaSpread() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [themes, setThemes] = useState([]);
  const [monthNames, setMonthNames] = useState([]);
  const [spread, setSpread] = useState({
    theme: 'garden', title: '', birthdays: [], photos: [], notes: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const fileInputRef = useRef(null);

  const currentTheme = useMemo(
    () => themes.find((t) => t.id === spread.theme) || themes[0],
    [themes, spread.theme]
  );

  useEffect(() => {
    axios.get(`${API}/zinnia/themes`, { withCredentials: true }).then((r) => {
      setThemes(r.data.themes || []);
      setMonthNames(r.data.month_names || []);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setDirty(false);
    try {
      const { data } = await axios.get(`${API}/zinnia/month/${year}/${month}`, { withCredentials: true });
      if (data.spread) {
        setSpread({
          theme: data.spread.theme || 'garden',
          title: data.spread.title || '',
          birthdays: data.spread.birthdays || [],
          photos: data.spread.photos || [],
          notes: data.spread.notes || [],
        });
      } else {
        setSpread({ theme: 'garden', title: '', birthdays: [], photos: [], notes: [] });
      }
    } catch (e) {
      toast.error('Kunde inte ladda månad');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/zinnia/month`, {
        year, month, ...spread,
      }, { withCredentials: true });
      toast.success('Sparat 🌸');
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    if (dirty && !window.confirm('Osparade ändringar. Byta ändå?')) return;
    setYear(y); setMonth(m);
  };

  const patch = (key, value) => {
    setSpread((s) => ({ ...s, [key]: value }));
    setDirty(true);
  };

  // Birthdays
  const addBirthday = () => patch('birthdays', [...spread.birthdays, { date: '', label: '', emoji: '🎂' }]);
  const updateBirthday = (i, k, v) => {
    const arr = [...spread.birthdays];
    arr[i] = { ...arr[i], [k]: v };
    patch('birthdays', arr);
  };
  const removeBirthday = (i) => patch('birthdays', spread.birthdays.filter((_, idx) => idx !== i));

  // Photos
  const addPhotoUrl = () => {
    const url = window.prompt('Klistra in bild-URL:');
    if (!url) return;
    patch('photos', [...spread.photos, { url: url.trim(), caption: '' }]);
  };
  const handleFile = (file) => {
    if (!file || !file.type?.startsWith('image/')) return;
    if (file.size > 700000) {
      toast.error('Bilden är för stor (max ~700KB)'); return;
    }
    const reader = new FileReader();
    reader.onload = () => patch('photos', [...spread.photos, { url: reader.result, caption: '' }]);
    reader.readAsDataURL(file);
  };
  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith('image/')) {
        const blob = it.getAsFile();
        handleFile(blob);
        e.preventDefault();
        return;
      }
    }
  };
  const updatePhoto = (i, k, v) => {
    const arr = [...spread.photos];
    arr[i] = { ...arr[i], [k]: v };
    patch('photos', arr);
  };
  const removePhoto = (i) => patch('photos', spread.photos.filter((_, idx) => idx !== i));

  // Notes
  const addNote = () => patch('notes', [...spread.notes, { text: '', color: NOTE_COLORS[spread.notes.length % NOTE_COLORS.length] }]);
  const updateNote = (i, k, v) => {
    const arr = [...spread.notes];
    arr[i] = { ...arr[i], [k]: v };
    patch('notes', arr);
  };
  const removeNote = (i) => patch('notes', spread.notes.filter((_, idx) => idx !== i));

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar månaden…</div>;

  const monthLabel = monthNames[month - 1] || '';
  const darkText = currentTheme?.dark_text;

  return (
    <div className="space-y-4" data-testid="zinnia-spread" onPaste={handlePaste}>
      {/* Header with month nav */}
      <div className="neo-card p-4 flex items-center justify-between flex-wrap gap-2 bg-white dark:bg-[#252545]">
        <div className="flex items-center gap-2">
          <button onClick={() => changeMonth(-1)} className="neo-btn p-2 bg-white" data-testid="zinnia-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-center min-w-[180px]">
            <p className="text-[10px] uppercase tracking-widest opacity-60">{year}</p>
            <p className="text-2xl font-bold font-['Fredoka']" data-testid="zinnia-month-name">{monthLabel}</p>
          </div>
          <button onClick={() => changeMonth(1)} className="neo-btn p-2 bg-white" data-testid="zinnia-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowThemePicker(true)}
            className="neo-btn px-3 py-2 bg-[#FFE8A3] text-xs font-bold flex items-center gap-1"
            data-testid="zinnia-theme-btn">
            <Palette className="w-3.5 h-3.5" /> {currentTheme?.emoji} {currentTheme?.label}
          </button>
          <button onClick={save} disabled={saving || !dirty}
            className={`neo-btn px-4 py-2 text-xs font-bold flex items-center gap-1 ${dirty ? 'bg-[#BBF7D0]' : 'bg-gray-100 opacity-60'}`}
            data-testid="zinnia-save-btn">
            <Save className="w-3.5 h-3.5" /> {saving ? 'Sparar…' : dirty ? 'Spara' : 'Sparat'}
          </button>
        </div>
      </div>

      {/* Themed canvas */}
      <div className="neo-card p-6 relative overflow-hidden"
        style={{ background: currentTheme?.gradient, color: darkText ? '#fff' : '#111' }}
        data-testid="zinnia-canvas">
        <input value={spread.title}
          onChange={(e) => patch('title', e.target.value)}
          placeholder={`${monthLabel} ${year}`}
          className="w-full bg-transparent border-b-2 border-black/20 focus:border-black/60 outline-none text-3xl font-bold font-['Fredoka'] py-1"
          style={{ color: darkText ? '#fff' : '#111' }}
          data-testid="zinnia-title-input" />
        <p className="text-xs opacity-70 mt-1">
          {currentTheme?.emoji} {currentTheme?.label} · Fyll i, klistra in, dröm dig bort ✨
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          {/* Birthdays */}
          <section className="rounded-2xl border-2 border-black bg-white/85 dark:bg-black/40 p-3 backdrop-blur-sm" style={{ color: '#111' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold font-['Fredoka'] flex items-center gap-1 text-sm">
                <Cake className="w-4 h-4" /> Födelsedagar & viktiga datum
              </p>
              <button onClick={addBirthday} className="neo-btn p-1 bg-white" data-testid="zinnia-add-birthday">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {spread.birthdays.length === 0 && <p className="text-[11px] opacity-60 italic">Tryck + för att lägga till.</p>}
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {spread.birthdays.map((b, i) => (
                <div key={i} className="flex gap-1 items-center" data-testid={`zinnia-birthday-${i}`}>
                  <input value={b.emoji || ''} onChange={(e) => updateBirthday(i, 'emoji', e.target.value)}
                    className="w-9 px-1 py-1 rounded-lg border-2 border-black bg-white text-center text-sm" maxLength={2} />
                  <input value={b.date || ''} onChange={(e) => updateBirthday(i, 'date', e.target.value)}
                    className="w-16 px-1.5 py-1 rounded-lg border-2 border-black bg-white text-xs"
                    placeholder="17" maxLength={10} />
                  <input value={b.label || ''} onChange={(e) => updateBirthday(i, 'label', e.target.value)}
                    className="flex-1 min-w-0 px-2 py-1 rounded-lg border-2 border-black bg-white text-xs"
                    placeholder="Mamma fyller år" maxLength={60} />
                  <button onClick={() => removeBirthday(i)} className="p-1 opacity-60 hover:opacity-100">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Photos */}
          <section className="rounded-2xl border-2 border-black bg-white/85 dark:bg-black/40 p-3 backdrop-blur-sm" style={{ color: '#111' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold font-['Fredoka'] flex items-center gap-1 text-sm">
                <ImageIcon className="w-4 h-4" /> Foton
              </p>
              <div className="flex gap-1">
                <button onClick={() => fileInputRef.current?.click()} className="neo-btn p-1 bg-white" data-testid="zinnia-upload-photo" title="Ladda upp">
                  <Upload className="w-3.5 h-3.5" />
                </button>
                <button onClick={addPhotoUrl} className="neo-btn p-1 bg-white" data-testid="zinnia-add-url" title="Klistra URL">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" hidden
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
            {spread.photos.length === 0 && (
              <p className="text-[11px] opacity-60 italic">
                Ladda upp, klistra in bild (Ctrl+V) eller en URL 📸
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
              {spread.photos.map((p, i) => (
                <div key={i} className="relative group" data-testid={`zinnia-photo-${i}`}>
                  <img src={p.url} alt=""
                    className="w-full aspect-square object-cover rounded-lg border-2 border-black" />
                  <input value={p.caption || ''}
                    onChange={(e) => updatePhoto(i, 'caption', e.target.value)}
                    placeholder="…"
                    className="w-full mt-1 px-1.5 py-0.5 rounded border-2 border-black bg-white text-[10px]" maxLength={80} />
                  <button onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 neo-btn p-1 bg-white opacity-0 group-hover:opacity-100 transition">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Notes */}
          <section className="rounded-2xl border-2 border-black bg-white/85 dark:bg-black/40 p-3 backdrop-blur-sm" style={{ color: '#111' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="font-bold font-['Fredoka'] flex items-center gap-1 text-sm">
                <StickyNote className="w-4 h-4" /> Anteckningar
              </p>
              <button onClick={addNote} className="neo-btn p-1 bg-white" data-testid="zinnia-add-note">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {spread.notes.length === 0 && <p className="text-[11px] opacity-60 italic">Tryck + för att lägga till post-it.</p>}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {spread.notes.map((n, i) => (
                <div key={i} className="rounded-xl border-2 border-black p-2 shadow-[2px_2px_0_#000]"
                  style={{ backgroundColor: n.color || '#FFF3B0' }}
                  data-testid={`zinnia-note-${i}`}>
                  <textarea value={n.text || ''} onChange={(e) => updateNote(i, 'text', e.target.value)}
                    rows={3}
                    placeholder="Skriv fritt här…"
                    className="w-full bg-transparent outline-none text-xs resize-none" />
                  <div className="flex items-center gap-1 mt-1">
                    {NOTE_COLORS.map((c) => (
                      <button key={c} onClick={() => updateNote(i, 'color', c)}
                        className={`w-4 h-4 rounded-full border-2 border-black ${n.color === c ? 'scale-125' : ''}`}
                        style={{ backgroundColor: c }} />
                    ))}
                    <button onClick={() => removeNote(i)} className="ml-auto p-0.5">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Theme picker */}
      {showThemePicker && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowThemePicker(false)} data-testid="zinnia-theme-picker">
          <div className="neo-card bg-white dark:bg-[#252545] max-w-lg w-full p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold font-['Fredoka'] mb-3">Välj tema för {monthLabel}</h3>
            <div className="grid grid-cols-2 gap-2">
              {themes.map((t) => (
                <button key={t.id}
                  onClick={() => { patch('theme', t.id); setShowThemePicker(false); }}
                  className={`p-4 rounded-2xl border-2 border-black text-left transition hover:scale-[1.02] ${spread.theme === t.id ? 'ring-4 ring-pink-400' : ''}`}
                  style={{ background: t.gradient, color: t.dark_text ? '#fff' : '#111' }}
                  data-testid={`zinnia-theme-${t.id}`}>
                  <p className="text-2xl">{t.emoji}</p>
                  <p className="text-sm font-bold font-['Fredoka']">{t.label}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZinniaSpread;
